import { useSetAtom, useAtomValue, useAtom } from 'jotai'
import { useEffect } from 'react'
import { classroomsAtom, sessionsAtom, currentSessionIdAtom, historyResetSessionIdsAtom, isSessionsLoadingAtom } from '../state/store'
import { saveCurrentSessionId } from '../lib/storage'
import { backendChatToSession, backendMessageToChat, mergeHistoryMessages } from '../state/types'
import type { BackendMessage } from '../state/types'
import { fetchChats, fetchClasses, fetchClassChats } from '../api/backend'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { useWebSocket } from '../contexts/WebSocketContext'

/**
 * One-time session initialization hook. Must be called exactly ONCE
 * in the chat layout to avoid duplicate API calls.
 *
 * Handles:
 * - Loading sessions from backend when workspace is resolved
 * - Persisting currentSessionId to localStorage
 * - Registering WebSocket handler for history messages
 *
 * NOTE: WS subscribe/unsubscribe is handled by useChatRouteSync in
 * the route page components. This hook no longer auto-subscribes.
 */
export function useSessionsInit() {
  const setSessions = useSetAtom(sessionsAtom)
  const setClassrooms = useSetAtom(classroomsAtom)
  const setSessionsLoading = useSetAtom(isSessionsLoadingAtom)
  const currentSessionId = useAtomValue(currentSessionIdAtom)
  const [historyResetSessionIds, setHistoryResetSessionIds] = useAtom(historyResetSessionIdsAtom)
  const { getAccessToken } = useAuth()
  const { isResolved, memberRole } = useWorkspace()
  const { addMessageHandler } = useWebSocket()

  // Persist current session ID to localStorage
  useEffect(() => {
    saveCurrentSessionId(currentSessionId)
  }, [currentSessionId])

  // Load sessions from backend API once workspace is resolved
  useEffect(() => {
    if (!isResolved) return
    const token = getAccessToken()
    if (!token) return
    setSessionsLoading(true)
    Promise.all([
      fetchChats(token).catch((error) => {
        console.error('Failed to load general chats:', error)
        return []
      }),
      fetchClasses(token),
    ])
      .then(async ([generalChats, classes]) => {
        setClassrooms(classes)
        const visibleClasses = memberRole === 'STUDENT'
          ? classes.filter((classroom) => classroom.isJoined)
          : classes
        const chatsByClass = await Promise.all(
          visibleClasses.map(async (classroom) => {
            try {
              const chats = await fetchClassChats(token, classroom.id)
              return chats.map((chat) => ({
                ...chat,
                classId: classroom.id,
                classTitle: classroom.title,
              }))
            } catch (error) {
              console.error(`Failed to load chats for class ${classroom.id}:`, error)
              return []
            }
          }),
        )

        const classChats = chatsByClass.flat()
        const classChatIds = new Set(classChats.map((chat) => chat.id))
        const standaloneChats = generalChats.filter((chat) => !classChatIds.has(chat.id))

        const sessions = [...standaloneChats, ...classChats]
          .map(backendChatToSession)
          .sort((a, b) => b.lastUsedAt - a.lastUsedAt)

        setSessions(sessions)
      })
      .catch((err) => {
        console.error('Failed to load student classes:', err)
      })
      .finally(() => {
        setSessionsLoading(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResolved, memberRole])

  // Register WebSocket handler for history messages
  useEffect(() => {
    return addMessageHandler((msg) => {
      if (msg.type !== 'history') return

      // The server may nest messages under `payload.messages`, `payload`,
      // or even at the root `msg.messages`. Handle all shapes gracefully.
      const raw = msg as Record<string, unknown>
      const payload = (raw.payload ?? raw) as Record<string, unknown>
      const rawMessages = (
        payload.messages ??
        (Array.isArray(payload) ? payload : null)
      ) as BackendMessage[] | null

      const chatId =
        (payload.chatId as string | undefined) ??
        rawMessages?.[0]?.chatId
      if (!chatId) return

      const messages = (rawMessages ?? []).map(backendMessageToChat)
      const hasMore =
        typeof payload.hasMore === 'boolean'
          ? payload.hasMore
          : messages.length > 0

      setSessions((prev) => {
        return prev.map((s) => {
          if (s.id !== chatId) return s
          return {
            ...s,
            messages: historyResetSessionIds.has(chatId)
              ? messages
              : mergeHistoryMessages(s.messages, messages),
            hasMore,
          }
        })
      })
      setHistoryResetSessionIds((prevIds: Set<string>) => {
        if (!prevIds.has(chatId)) return prevIds
        const next = new Set(prevIds)
        next.delete(chatId)
        return next
      })
    })
  }, [addMessageHandler, historyResetSessionIds, setHistoryResetSessionIds, setSessions])

  return {}
}
